/**
 * /admin/ledgers/:id — Ledger Statement.
 * @file src/web/features/admin/pages/ledgers/[id].tsx
 *
 * One ledger's transactions in date order with a running balance
 * (opening → closing), Dr/Cr columns. Reached by clicking a ledger from
 * the Ledgers list or the Trial Balance.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, BookOpenText } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';
import { narrationText } from '../../../../shared/utils/narration';

interface Line {
  voucherId: string; date: string; type: string; number: string | null; narration: string | null;
  /** The ledger this entry was posted against — the contra side. */
  particulars: string | null; particularsMore: number;
  debit: number; credit: number; balance: number; balanceDc: string;
}
interface Statement {
  ledger: { id: string; name: string; group: string | null };
  opening: { amount: number; dc: string };
  closing: { amount: number; dc: string };
  totals: { debit: number; credit: number; count: number };
  lines: Line[];
}

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const dcCell = (amt: number, dc: string) => (dc ? `${inr(amt)} ${dc}` : '—');

export default function LedgerStatementPage() {
  const { id } = useParams<{ id: string }>();
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [data, setData] = useState<Statement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setData(null); setError(null);
    adminFetchJson<Statement>(`/api/tally-import/ledgers/${id}/statement`, { query: { entityId: entityId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [id, entityId, from, to]);

  return (
    <>
      <SEO title={data ? data.ledger.name : 'Ledger'} />
      <AdminPageHeader title={data?.ledger.name ?? 'Ledger'} breadcrumbs={[{ label: 'Books', href: '/admin/books' }, { label: 'Ledgers', href: '/admin/ledgers' }, { label: 'Statement' }]} />

      <Link to="/admin/ledgers" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700">
        <ChevronLeft className="h-4 w-4" /> Ledgers
      </Link>

      <PeriodBar />

      {error &&<div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label="Loading ledger" />}

      {data && (
        <>
          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            <Stat label="Group" value={data.ledger.group ?? '—'} />
            <Stat label="Opening balance" value={dcCell(data.opening.amount, data.opening.dc)} />
            <Stat label="Closing balance" value={dcCell(data.closing.amount, data.closing.dc)} strong />
          </div>

          {data.lines.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
              <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><BookOpenText className="h-6 w-6" /></span>
              <h3 className="mt-5 text-lg font-semibold text-ink-900">No transactions</h3>
              <p className="mt-1.5 text-sm text-ink-600">This ledger has an opening balance but no vouchers in the imported period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-400">
                    <th className="px-5 py-3 text-left font-medium">Date</th>
                    <th className="px-5 py-3 text-left font-medium">Particulars</th>
                    <th className="px-5 py-3 text-left font-medium">Vch type</th>
                    <th className="px-5 py-3 text-left font-medium">Vch no</th>
                    <th className="px-5 py-3 text-right font-medium">Debit</th>
                    <th className="px-5 py-3 text-right font-medium">Credit</th>
                    <th className="px-5 py-3 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-50">
                  {data.opening.dc && (
                    <tr className="text-ink-500">
                      <td className="px-5 py-2.5 whitespace-nowrap">—</td>
                      <td className="px-5 py-2.5 italic">Opening balance</td>
                      <td className="px-5 py-2.5" />
                      <td className="px-5 py-2.5" />
                      <td className="px-5 py-2.5" />
                      <td className="px-5 py-2.5" />
                      <td className="px-5 py-2.5 text-right tabular-nums">{dcCell(data.opening.amount, data.opening.dc)}</td>
                    </tr>
                  )}
                  {data.lines.map((l, i) => (
                    <tr key={i} className="hover:bg-ink-50/60">
                      <td className="px-5 py-2.5 whitespace-nowrap text-ink-500">{new Date(l.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                      <td className="px-5 py-2.5">
                        <span className="font-medium text-ink-800">{l.particulars ?? '—'}</span>
                        {l.particularsMore > 0 && <span className="ml-1.5 text-xs text-ink-400">+{l.particularsMore} more</span>}
                        {narrationText(l.narration) && <span className="block truncate text-xs text-ink-400">{narrationText(l.narration)}</span>}
                      </td>
                      <td className="px-5 py-2.5 whitespace-nowrap text-ink-600">{l.type}</td>
                      <td className="px-5 py-2.5 whitespace-nowrap text-ink-500">{l.number || '—'}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(l.debit)}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(l.credit)}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-ink-600">{dcCell(l.balance, l.balanceDc)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
                    <td className="px-5 py-3" colSpan={4}>Total ({data.totals.count} entries)</td>
                    <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.debit)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{inr(data.totals.credit)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{dcCell(data.closing.amount, data.closing.dc)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
      <p className={`mt-1 tabular-nums ${strong ? 'text-lg font-bold text-ink-900' : 'text-sm font-medium text-ink-700'}`}>{value}</p>
    </div>
  );
}
